// Apolo IDE — PlanCard (sprint plan-tab-v1, E2).
//
// Wrapper drag genérico pra qualquer block kind. Edição inline simples
// neste epic — E3 vai adicionar editores ricos (markdown, image upload,
// embed sandboxes etc). Por hora cobre text/sticky/heading/link/skill
// /divider/todo/file/image/video com inputs nativos.

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, MoreHorizontal, Trash2 } from 'lucide-react';
import { getBlockKindMeta } from '../../lib/plan/blockKinds.js';
import PlanBlockBody from './PlanBlockBody.jsx';

const RESIZE_HANDLE = 12;

export default function PlanCard({
  block,
  zoom,
  selected,
  onSelect,
  onPositionChange,
  onSizeChange,
  onPatch,
  onRemove,
  onConnectStart,
  connectMode = false,
}) {
  const meta = getBlockKindMeta(block.kind);
  const [menuOpen, setMenuOpen] = useState(false);
  const cardRef = useRef(null);
  const [resizing, setResizing] = useState(false);
  const resizeStart = useRef(null);
  // Drag manual (pointer events) — substitui o Framer `drag` que causava
  // dupla aplicação de posição (transform + left/top = "pulo").
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);

  function handleCardPointerDown(e) {
    if (e.button !== 0) return;
    // Não inicia drag em elementos interativos nem nos handles/resize.
    if (e.target.closest?.('input, textarea, button, a, [role="button"], [role="menuitem"], .cm-editor, [data-block-handle]')) return;
    e.preventDefault();
    dragStart.current = { px: e.clientX, py: e.clientY, x: block.x, y: block.y };
    setDragging(true);
    onSelect && onSelect();
  }

  useEffect(() => {
    if (!dragging) return undefined;
    function move(e) {
      if (!dragStart.current) return;
      const dx = (e.clientX - dragStart.current.px) / zoom;
      const dy = (e.clientY - dragStart.current.py) / zoom;
      onPositionChange({ x: dragStart.current.x + dx, y: dragStart.current.y + dy });
    }
    function up() { setDragging(false); dragStart.current = null; }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [dragging, zoom, onPositionChange]);

  // Resize via canto inferior direito.
  useEffect(() => {
    if (!resizing) return;
    function move(e) {
      if (!resizeStart.current) return;
      const dx = (e.clientX - resizeStart.current.startX) / zoom;
      const dy = (e.clientY - resizeStart.current.startY) / zoom;
      onSizeChange({
        w: resizeStart.current.w + dx,
        h: resizeStart.current.h + dy,
      });
    }
    function up() { setResizing(false); resizeStart.current = null; }
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [resizing, zoom, onSizeChange]);

  const accent = block.color || meta.color;

  return (
    <motion.div
      ref={cardRef}
      data-block-id={block.id}
      data-block-kind={block.kind}
      role="group"
      aria-label={`${meta.label}: ${blockTitle(block)}`}
      onPointerDown={handleCardPointerDown}
      style={{
        position: 'absolute',
        left: block.x,
        top: block.y,
        width: block.w,
        height: block.h,
        zIndex: block.z ?? 0,
        cursor: dragging ? 'grabbing' : undefined,
        touchAction: 'none',
      }}
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onSelect && onSelect(); }}
      className={`group rounded-xl border bg-apolo-surface1 text-apolo-textPrimary shadow-sm transition-colors ${
        selected
          ? 'border-apolo-plan-accentBorder'
          : 'border-apolo-borderSubtle hover:border-apolo-borderStrong'
      }`}
    >
      {/* Header */}
      <div
        className="flex h-7 items-center gap-1 rounded-t-xl px-2 text-[11px] font-medium uppercase tracking-wide"
        style={{
          background: `linear-gradient(180deg, ${withAlpha(accent, 0.14)} 0%, transparent 100%)`,
          color: accent,
          borderBottom: `1px solid ${withAlpha(accent, 0.18)}`,
        }}
      >
        <span aria-hidden="true" className="inline-flex h-5 w-3 cursor-grab items-center justify-center text-apolo-textTertiary active:cursor-grabbing">
          <GripVertical size={12} />
        </span>
        <span className="truncate">{meta.label}</span>
        <span className="ml-auto inline-flex items-center gap-0.5">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary group-hover:opacity-100 focus:outline-none focus-visible:opacity-100"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Ações do bloco"
          >
            <MoreHorizontal size={12} />
          </button>
        </span>
      </div>

      {/* Body */}
      <div className="relative h-[calc(100%-1.75rem)] overflow-hidden rounded-b-xl">
        <PlanBlockBody
          block={block}
          accent={accent}
          onPayloadChange={(payload) => onPatch({ payload })}
        />
      </div>

      {/* Connection handles — 4 bordas, aparecem no hover. Arraste pra ligar. */}
      {onConnectStart ? (
        [
          { side: 'top',    cls: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2' },
          { side: 'right',  cls: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2' },
          { side: 'bottom', cls: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2' },
          { side: 'left',   cls: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2' },
        ].map((h) => (
          <span
            key={h.side}
            role="button"
            data-block-handle
            aria-label={`Conectar a partir do lado ${h.side}`}
            title="Arraste pra conectar a outro bloco"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onConnectStart(block.id, e); }}
            className={`absolute z-30 h-3 w-3 cursor-crosshair rounded-full border-2 border-apolo-surface1 opacity-0 shadow transition hover:scale-125 group-hover:opacity-100 ${h.cls}`}
            style={{ background: accent }}
          />
        ))
      ) : null}

      {/* Drop target ring quando há uma conexão sendo arrastada */}
      {connectMode ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-apolo-plan-accent ring-offset-2 ring-offset-apolo-bg"
        />
      ) : null}

      {/* Resize handle */}
      <span
        aria-hidden="true"
        data-block-handle
        onPointerDown={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => {
          e.stopPropagation();
          resizeStart.current = { startX: e.clientX, startY: e.clientY, w: block.w, h: block.h };
          setResizing(true);
        }}
        className="absolute bottom-1 right-1 cursor-se-resize text-apolo-textTertiary opacity-0 transition group-hover:opacity-60 hover:opacity-100"
        style={{ width: RESIZE_HANDLE, height: RESIZE_HANDLE }}
      >
        <svg viewBox="0 0 16 16" width={12} height={12} fill="currentColor">
          <path d="M14 14h-2v-2h2v2zm0-4h-2V8h2v2zm-4 4H8v-2h2v2z" />
        </svg>
      </span>

      {/* Dropdown menu */}
      {menuOpen ? (
        <div role="menu" className="absolute right-2 top-7 z-50 w-40 overflow-hidden rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-sm shadow-lg">
          <button
            type="button"
            role="menuitem"
            onClick={(e) => { e.stopPropagation(); onPatch({ payload: { ...block.payload, _duplicate_at: Date.now() } }); setMenuOpen(false); }}
            className="block w-full px-3 py-1.5 text-left text-xs text-apolo-textPrimary hover:bg-apolo-surface3"
          >
            Trazer pra frente
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={(e) => { e.stopPropagation(); onRemove(); setMenuOpen(false); }}
            className="flex w-full items-center gap-2 border-t border-apolo-borderSubtle px-3 py-1.5 text-left text-xs text-apolo-error hover:bg-apolo-errorBg/30"
          >
            <Trash2 size={11} /> Apagar bloco
          </button>
        </div>
      ) : null}
    </motion.div>
  );
}

function blockTitle(block) {
  const payload = block.payload || {};
  return payload.text || payload.title || payload.url || payload.body?.slice?.(0, 40) || block.kind;
}

function withAlpha(color, alpha) {
  if (!color || typeof color !== 'string') return `rgba(139,92,246,${alpha})`;
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}
